import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { isInitialView } from '../lib/initialView';

/**
 * Scroll-reveal animace – prvek se plynule objeví při najetí do viewportu.
 * Základ pro animovanější styl webu; respektuje prefers-reduced-motion.
 *
 * Použití:
 *   <Reveal>...</Reveal>
 *   <Reveal delay={120} variant="reveal-scale">...</Reveal>
 *   <Reveal as="section" className="py-12">...</Reveal>
 */
const Reveal = ({ children, delay = 0, variant = '', className = '', as: Tag = 'div', ...rest }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [instant, setInstant] = useState(false);

  // Při vstupu na web se to, co je hned na obrazovce, ukáže bez animace –
  // ještě před prvním vykreslením (layout effect). Jinak by hero na chvíli
  // zmizel a prohlížeč by ho nepočítal do LCP (viz lib/initialView.js).
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !isInitialView()) return;
    const r = el.getBoundingClientRect();
    if (r.top < window.innerHeight && r.bottom > 0) {
      setInstant(true);
      setVisible(true);
    }
  }, []);

  useEffect(() => {
    if (visible) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    if (!('IntersectionObserver' in window)) {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible]);

  return (
    <Tag
      ref={ref}
      className={`reveal ${variant} ${visible ? 'reveal-visible' : ''} ${instant ? 'reveal-instant' : ''} ${className}`}
      style={{ '--reveal-delay': `${delay}ms` }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
